/**
 * usePlatformSelection Hook
 * Manages platform selection state and panel collapse/expand
 */

import { useState, useEffect, useCallback } from 'react';
import { PlatformType } from '../types/platform.types';

const STORAGE_KEY = 'omnimind_selected_platforms';
const COLLAPSE_KEY = 'omnimind_panel_collapsed';

export function usePlatformSelection() {
  // Initialize selected platforms from localStorage or default to "All"
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformType[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Failed to load platform selection from localStorage:', err);
    }
    return ['all']; // Default to "All Platforms"
  });

  // Initialize collapse state from localStorage or default based on screen size
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(COLLAPSE_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
    } catch (err) {
      console.error('Failed to load panel state from localStorage:', err);
    }
    // Default: collapsed on mobile (<1024px), expanded on desktop
    return window.innerWidth < 1024;
  });

  // Persist selected platforms to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedPlatforms));
    } catch (err) {
      console.error('Failed to save platform selection to localStorage:', err);
    }
  }, [selectedPlatforms]);

  // Persist collapse state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(isCollapsed));
    } catch (err) {
      console.error('Failed to save panel state to localStorage:', err);
    }
  }, [isCollapsed]);

  /**
   * Select a platform
   * Logic:
   * - If "All" is clicked → deselect all others, select only "All"
   * - If specific platform is clicked → deselect "All", toggle that platform
   * - If no platforms remain selected → default back to "All"
   */
  const selectPlatform = useCallback((platformId: PlatformType) => {
    setSelectedPlatforms((prev) => {
      // If "All" is clicked
      if (platformId === 'all') {
        return ['all'];
      }

      // If a specific platform is clicked
      const newSelection = prev.filter((id) => id !== 'all'); // Remove "All" first

      // Toggle the platform
      if (newSelection.includes(platformId)) {
        // Remove if already selected
        const updated = newSelection.filter((id) => id !== platformId);
        // If no platforms left, default to "All"
        return updated.length === 0 ? ['all'] : updated;
      } else {
        // Add platform
        return [...newSelection, platformId];
      }
    });
  }, []);

  /**
   * Toggle panel collapse/expand
   */
  const togglePanel = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  /**
   * Reset selection to "All Platforms"
   */
  const reset = useCallback(() => {
    setSelectedPlatforms(['all']);
  }, []);

  // Computed values
  const isAllSelected = selectedPlatforms.includes('all');
  const hasActiveSelection = selectedPlatforms.length > 0;
  const selectedCount = isAllSelected ? 0 : selectedPlatforms.length;

  return {
    selectedPlatforms,
    isCollapsed,
    selectPlatform,
    togglePanel,
    reset,
    isAllSelected,
    hasActiveSelection,
    selectedCount,
  };
}
